import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import {
  AlarmClock, Bell, BellOff, Check, CheckCircle2, ClipboardCopy, ExternalLink, FileText, ListTree, MapPin,
  MessageSquarePlus, Pencil, RefreshCw, Settings2, Wrench,
} from 'lucide-react'
import { usePermissions, useToast } from '../../admin/primitives.js'
import { useConfirm } from '../../shell/confirm.js'
import {
  copierTexte, useDialogueMenu, useMenuContextuel, type ActionMenu, type GroupeMenu,
} from '../../shell/menu-contextuel.js'
import { useGroupeFavori } from '../../shell/menu-favori.js'
import { navigate } from '../../router.js'
import { fmtHeure, lireSupervision } from '../api.js'
import { EVENEMENT_SONDE_ECRITE, nomSalle, ordreSalle } from './commun.js'
import { cheminAppareil, cheminIncident, cheminRegistreAppareil } from './fiches/modele.js'
import { CONFIRMER_NUIT, envoyer, FormulaireAnnotation, sourdineIncident, ZoneTexte, type Sourdine } from './fiches/Formulaires.js'
import { ecrireSonde, estConflitVersion } from './registre/ecrire.js'
import { estFicheEdition } from './registre/modele.js'
import { prochainHuitHeures } from './menu-sonde-modele.js'
import { actualisationEnCours, actualiser, type CibleActualisation } from './actualiser.js'
import { cibleValide } from './actualiser-modele.js'

/**
 * MENU CLIC DROIT DE LA SUPERVISION (24/10/2026) — appareils et incidents.
 *
 * Chaque entrée reprend un geste qui existe déjà dans les fiches, par la
 * même route du relais, avec le même droit :
 *
 *   · Changer de salle, Renommer   → PUT devices/:uid (registre, If-Match)
 *   · Mettre en maintenance        → POST maintenance (scope device)
 *   · Ajouter une note             → POST annotations (régie)
 *   · Acquitter, Résoudre, Noter   → POST incidents/:id/ack|resolve|note
 *   · Sourdine (30 min, la nuit)   → POST|DELETE incidents/:id/snooze
 *   · Actualiser (25/10/2026)      → POST devices/:uid/refresh, le bouton de la
 *                                    fiche appareil (actualiser.tsx, sonde.read)
 *
 * Le mode sûr, le step-up et l'audit restent ceux du relais. Une entrée
 * n'apparaît que si le compte a le droit ; le serveur reste l'arbitre.
 *
 * Un appareil s'ajoute aux favoris (lot 6) par l'épingle de l'étoile
 * (useGroupeFavori), sous son nom ; un incident jamais (décision d'Hugo).
 */

export interface AppareilMenu { uid: string; nom: string; zone_code?: string | null }
/**
 * `acquitte` et `sourdine` absents = état inconnu de la vue (nuit, fiche
 * salle) : l'entrée correspondante n'est pas proposée, plutôt que d'acquitter
 * deux fois ou d'écourter sans le savoir une sourdine de nuit.
 */
export interface IncidentMenu { id: string; titre: string; status?: string | null; acquitte?: boolean; sourdine?: string | null }

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)
const signalerEcriture = () => window.dispatchEvent(new Event(EVENEMENT_SONDE_ECRITE))

/* ─── salles : une lecture partagée par tous les menus de la page ─── */

type Salle = { code: string; label: string }
let salles: Salle[] | null = null
let sallesLuesA = 0
let lectureSalles: Promise<void> | null = null
const abonnesSalles = new Set<() => void>()
const estSalles = (x: unknown): x is { zones: Salle[] } =>
  objet(x) && Array.isArray(x['zones']) && (x['zones'] as unknown[]).every((z) => objet(z) && typeof z['code'] === 'string')

function relireSalles(): void {
  if (lectureSalles || Date.now() - sallesLuesA < 300_000) return
  lectureSalles = lireSupervision('/v/zones', estSalles).then((l) => {
    if (l.etat === 'ok') {
      salles = [...l.data.zones].sort((a, b) => ordreSalle(a.code) - ordreSalle(b.code) || nomSalle(a.code, a.label).localeCompare(nomSalle(b.code, b.label), 'fr'))
      sallesLuesA = Date.now()
    }
    lectureSalles = null
    abonnesSalles.forEach((f) => f())
  })
}

function useSalles(actif: boolean): Salle[] | null {
  const s = useSyncExternalStore((f) => { abonnesSalles.add(f); return () => { abonnesSalles.delete(f) } }, () => salles)
  useEffect(() => { if (actif) relireSalles() }, [actif])
  return s
}

/* ─── écritures ─── */

/** Relit l'ETag du registre puis écrit : un menu ne garde jamais de version périmée. */
async function modifierPoste(uid: string, ecart: Record<string, unknown>, rien: string): Promise<string | null> {
  const l = await lireSupervision(`/v/registry/devices/${encodeURIComponent(uid)}`, estFicheEdition)
  if (l.etat !== 'ok') return `${l.message} — ${rien}.`
  const e = await ecrireSonde('PUT', `devices/${encodeURIComponent(uid)}`, ecart, { etag: l.data.device.etag, rien })
  if (e.ok) return null
  if (estConflitVersion(e)) return `L’appareil vient d’être modifié ailleurs — ${rien}. Rouvrez le menu pour réessayer.`
  return e.message
}

const schemaEst = (scheme: string, quoi: 'ip' | 'mac') => {
  const s = scheme.toLowerCase()
  return s === quoi || s.endsWith(`_${quoi}`)
}

const heure = (d: Date) => new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' }).format(d)
const absolu = (chemin: string) => `${window.location.origin}${chemin}`

/**
 * Ouvre une fiche puis amène une de ses sections à l'écran. Le routeur ne
 * suit pas les ancres : la section n'existe qu'une fois la fiche lue, on
 * l'attend (3 s au plus) avant de la faire défiler.
 */
function allerA(chemin: string, ancre: string): void {
  navigate(`${chemin}#${ancre}`)
  const fin = Date.now() + 3000
  const chercher = () => {
    const el = document.getElementById(ancre)
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return }
    if (Date.now() < fin) requestAnimationFrame(chercher)
  }
  requestAnimationFrame(chercher)
}

/* ─── fenêtres ─── */

function Renommer({ a, fermer }: { a: AppareilMenu; fermer: () => void }) {
  const toast = useToast()
  const [nom, setNom] = useState(a.nom)
  const champ = useRef<HTMLInputElement>(null)
  // Après le focus que la fenêtre pose sur elle-même : sinon il ne tient pas.
  useEffect(() => { const t = setTimeout(() => { champ.current?.focus(); champ.current?.select() }, 0); return () => clearTimeout(t) }, [])
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const propre = nom.trim()
  const valider = async () => {
    if (!propre || propre === a.nom) return
    setEnCours(true); setErreur(null)
    const err = await modifierPoste(a.uid, { display_name: propre }, 'le nom n’a pas changé')
    setEnCours(false)
    if (err) { setErreur(err); return }
    toast('ok', `Renommé : ${propre}.`)
    signalerEcriture()
    fermer()
  }
  return (
    <form className="sv-fiche-form" onSubmit={(e) => { e.preventDefault(); void valider() }}>
      <div className="lc-field">
        <label className="lc-label" htmlFor="ctx-nom">Nom affiché</label>
        <input id="ctx-nom" ref={champ} className="lc-input" value={nom} maxLength={120} onChange={(e) => setNom(e.target.value)} />
      </div>
      <p className="sv-fiche-doux">Un nom saisi ici ne suit plus celui de la source (UniFi, Home Assistant…).</p>
      {erreur ? <p className="lc-form__error" role="alert">{erreur}</p> : null}
      <div className="sv-fiche-form__actions">
        <button type="button" className="lc-btn lc-btn--ghost" onClick={fermer}>Annuler</button>
        <button type="submit" className="lc-btn lc-btn--primary" disabled={enCours || !propre || propre === a.nom}>{enCours ? 'Enregistrement…' : 'Renommer'}</button>
      </div>
    </form>
  )
}

function Resoudre({ i, fermer }: { i: IncidentMenu; fermer: () => void }) {
  const toast = useToast()
  const confirm = useConfirm()
  const [raison, setRaison] = useState('')
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const valider = async () => {
    const r = raison.trim()
    if (!r) return
    const oui = await confirm({
      title: 'Résoudre cet incident à la main ?',
      message: 'L’incident est clos et toutes ses alertes sont terminées. Votre raison est gardée dans sa chronologie.',
      details: [{ label: 'Incident', value: i.titre }, { label: 'Raison', value: r.slice(0, 140) }],
      variant: 'sensitive',
      confirmLabel: 'Résoudre',
    })
    if (!oui) return
    setEnCours(true); setErreur(null)
    const err = await envoyer(`incidents/${encodeURIComponent(i.id)}/resolve`, { reason: r }, 'l’incident reste ouvert')
    setEnCours(false)
    if (err) { setErreur(err); return }
    toast('ok', 'Incident résolu.')
    signalerEcriture()
    fermer()
  }
  return (
    <div className="sv-fiche-form">
      <p className="sv-fiche-texte">{i.titre}</p>
      <ZoneTexte libelle="Pourquoi le clore" valeur={raison} onChange={setRaison} max={2000} placeholder="Ex. : faux positif, câble remplacé." />
      {erreur ? <p className="lc-form__error" role="alert">{erreur}</p> : null}
      <div className="sv-fiche-form__actions">
        <button type="button" className="lc-btn lc-btn--ghost" onClick={fermer}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={enCours || !raison.trim()} onClick={() => { void valider() }}>{enCours ? 'Résolution…' : 'Résoudre'}</button>
      </div>
    </div>
  )
}

function NoteIncident({ i, fermer }: { i: IncidentMenu; fermer: () => void }) {
  const toast = useToast()
  const [note, setNote] = useState('')
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const valider = async () => {
    if (!note.trim()) return
    setEnCours(true); setErreur(null)
    const err = await envoyer(`incidents/${encodeURIComponent(i.id)}/note`, { body: note.trim() }, 'la note n’a pas été ajoutée')
    setEnCours(false)
    if (err) { setErreur(err); return }
    toast('ok', 'Note ajoutée à l’incident.')
    signalerEcriture()
    fermer()
  }
  return (
    <div className="sv-fiche-form">
      <p className="sv-fiche-texte">{i.titre}</p>
      <ZoneTexte libelle="Note" valeur={note} onChange={setNote} max={4000} placeholder="Ce que vous avez vu ou fait." />
      {erreur ? <p className="lc-form__error" role="alert">{erreur}</p> : null}
      <div className="sv-fiche-form__actions">
        <button type="button" className="lc-btn lc-btn--ghost" onClick={fermer}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={enCours || !note.trim()} onClick={() => { void valider() }}>{enCours ? 'Envoi…' : 'Ajouter la note'}</button>
      </div>
    </div>
  )
}

/* ─── le hook ─── */

/**
 * Les déclencheurs du menu d'un appareil ou d'un incident :
 *
 *     const menu = useMenuSonde()
 *     <Link href=… {...menu.appareil({ uid, nom, zone_code })}>
 */
export function useMenuSonde() {
  const menu = useMenuContextuel()
  const dialogue = useDialogueMenu()
  const toast = useToast()
  const confirm = useConfirm()
  const { permissions } = usePermissions()
  const peutRegistre = permissions.includes('sonde.registry.manage') && permissions.includes('sonde.details.read')
  const peutDetails = permissions.includes('sonde.details.read')
  const peutAnnoter = permissions.includes('sonde.annotate')
  const peutAcquitter = permissions.includes('sonde.incident.ack')
  const peutLire = permissions.includes('sonde.read')
  const listeSalles = useSalles(peutRegistre)
  const favori = useGroupeFavori()

  const maintenance = useCallback(async (a: AppareilMenu, fin: Date, libelle: string) => {
    const oui = await confirm({
      title: `Mettre ${a.nom} en maintenance ?`,
      message: `Jusqu’à ${heure(fin)}, Sonde ne compte plus ses pannes dans l’état du club et n’envoie aucune alerte pour lui.`,
      details: [{ label: 'Appareil', value: a.nom }, { label: 'Durée', value: libelle }],
      variant: 'standard',
      confirmLabel: 'Mettre en maintenance',
    })
    if (!oui) return
    const e = await ecrireSonde('POST', 'maintenance', {
      scope_kind: 'device', scope_ref: a.uid, starts_at: new Date().toISOString(), ends_at: fin.toISOString(),
      reason: `Maintenance posée depuis le menu clic droit (${libelle}).`,
    }, { rien: 'aucune maintenance n’a été posée' })
    if (!e.ok) { toast('error', e.message); return }
    toast('ok', `${a.nom} est en maintenance jusqu’à ${heure(fin)}.`)
    signalerEcriture()
  }, [confirm, toast])

  const actionsAppareil = useCallback((a: AppareilMenu): GroupeMenu[] => {
    const fiche = cheminAppareil(a.uid)
    const groupes: GroupeMenu[] = [{
      actions: [
        { id: 'ouvrir', libelle: 'Ouvrir la fiche', icone: <FileText size={15} />, href: fiche },
        { id: 'onglet', libelle: 'Ouvrir dans un nouvel onglet', icone: <ExternalLink size={15} />, href: fiche, nouvelOnglet: true },
        ...(peutRegistre ? [{ id: 'registre', libelle: 'Modifier au registre', icone: <Settings2 size={15} />, href: cheminRegistreAppareil(a.uid) }] : []),
      ],
    }]
    const cible: CibleActualisation = { kind: 'device', uid: a.uid, nom: a.nom }
    if (peutLire && cibleValide(cible)) {
      groupes.push({ actions: [{
        id: 'actualiser', libelle: 'Actualiser', icone: <RefreshCw size={15} />, agir: () => { void actualiser(cible) },
        ...(actualisationEnCours(cible) ? { indisponible: 'Actualisation en cours' } : {}),
      }] })
    }
    if (peutRegistre) {
      if (listeSalles === null) relireSalles()
      const sousSalles: ActionMenu[] = (listeSalles ?? []).map((z) => {
        const nom = nomSalle(z.code, z.label)
        return {
          id: `salle-${z.code}`, libelle: nom, coche: z.code === a.zone_code,
          ...(z.code === a.zone_code ? { indisponible: 'Salle actuelle' } : {}),
          agir: async () => {
            const err = await modifierPoste(a.uid, { zone_code: z.code }, 'la salle n’a pas changé')
            if (err) { toast('error', err); return }
            toast('ok', `${a.nom} est maintenant en ${nom}.`)
            signalerEcriture()
          },
        }
      })
      const huit = prochainHuitHeures()
      const dans = (h: number) => new Date(Date.now() + h * 3_600_000)
      groupes.push({
        titre: 'Registre',
        actions: [
          { id: 'salle', libelle: 'Changer de salle', icone: <MapPin size={15} />, sousMenu: sousSalles,
            ...(listeSalles === null ? { indisponible: 'Liste des salles pas encore lue : rouvrez le menu' } : {}) },
          { id: 'renommer', libelle: 'Renommer…', icone: <Pencil size={15} />, agir: () => dialogue(`Renommer ${a.nom}`, (fermer) => <Renommer a={a} fermer={fermer} />) },
          { id: 'maintenance', libelle: 'Mettre en maintenance', icone: <Wrench size={15} />, sousMenu: [
            { id: 'm1', libelle: 'Pendant 1 h', agir: () => maintenance(a, dans(1), '1 h') },
            { id: 'm4', libelle: 'Pendant 4 h', agir: () => maintenance(a, dans(4), '4 h') },
            { id: 'm8', libelle: `Jusqu’à ${heure(huit)} (fin de nuit)`, agir: () => maintenance(a, huit, `jusqu’à ${heure(huit)}`) },
          ] },
        ],
      })
    }
    if (peutAnnoter) {
      groupes.push({ actions: [{
        id: 'note', libelle: 'Ajouter une note…', icone: <MessageSquarePlus size={15} />,
        agir: () => dialogue(`Note sur ${a.nom}`, (fermer) => (
          <FormulaireAnnotation cible={a.zone_code ? { device_uid: a.uid, zone_code: a.zone_code } : { device_uid: a.uid }}
            onFait={() => { toast('ok', 'Note enregistrée dans Sonde.'); signalerEcriture(); fermer() }} />
        )),
      }] })
    }
    const copier: ActionMenu[] = [
      { id: 'c-nom', libelle: 'Copier le nom', icone: <ClipboardCopy size={15} />, agir: () => copierTexte(a.nom, 'nom', toast) },
      { id: 'c-uid', libelle: 'Copier l’identifiant', icone: <ClipboardCopy size={15} />, agir: () => copierTexte(a.uid, 'identifiant', toast) },
      { id: 'c-lien', libelle: 'Copier le lien', icone: <ClipboardCopy size={15} />, agir: () => copierTexte(absolu(fiche), 'lien', toast) },
    ]
    if (peutDetails) {
      for (const [schema, quoi] of [['ip', 'adresse IP'], ['mac', 'adresse MAC']] as const) {
        copier.push({
          id: `c-${schema}`, libelle: `Copier l’${quoi}`, icone: <ClipboardCopy size={15} />,
          agir: async () => {
            const l = await lireSupervision(`/v/registry/devices/${encodeURIComponent(a.uid)}`, estFicheEdition)
            if (l.etat !== 'ok') { toast('error', l.message); return }
            // Les schémas réels : mac, unifi_device_mac, unifi_client_mac, ip, artnet_ip…
            const v = l.data.identifiers.find((x) => x.current && schemaEst(x.scheme, schema))?.value
            if (!v) { toast('error', `Sonde ne connaît pas d’${quoi} actuelle pour ${a.nom}.`); return }
            await copierTexte(v, quoi, toast)
          },
        })
      }
    }
    groupes.push(favori(fiche, a.nom))
    groupes.push({ titre: 'Copier', actions: copier })
    return groupes
  }, [peutRegistre, peutAnnoter, peutDetails, peutLire, listeSalles, dialogue, maintenance, toast, favori])

  const actionsIncident = useCallback((i: IncidentMenu): GroupeMenu[] => {
    const fiche = cheminIncident(i.id)
    const clos = i.status === 'resolved'
    const groupes: GroupeMenu[] = [{
      actions: [
        { id: 'ouvrir', libelle: 'Ouvrir l’incident', icone: <FileText size={15} />, href: fiche },
        { id: 'onglet', libelle: 'Ouvrir dans un nouvel onglet', icone: <ExternalLink size={15} />, href: fiche, nouvelOnglet: true },
        { id: 'touches', libelle: 'Voir les appareils touchés', icone: <ListTree size={15} />, agir: () => allerA(fiche, 'sv-inc-touches') },
      ],
    }]
    if (peutAcquitter) {
      const gestes: ActionMenu[] = []
      if (!clos && i.acquitte === false) {
        gestes.push({
          id: 'ack', libelle: 'Acquitter', icone: <Check size={15} />,
          agir: async () => {
            const err = await envoyer(`incidents/${encodeURIComponent(i.id)}/ack`, {}, 'rien n’a été acquitté')
            if (err) { toast('error', err); return }
            toast('ok', 'Incident acquitté : l’équipe sait que vous vous en occupez.')
            signalerEcriture()
          },
        })
      }
      if (!clos) gestes.push({ id: 'resolve', libelle: 'Résoudre…', icone: <CheckCircle2 size={15} />, agir: () => dialogue('Résoudre l’incident', (fermer) => <Resoudre i={i} fermer={fermer} />) })
      gestes.push({ id: 'note', libelle: 'Ajouter une note…', icone: <MessageSquarePlus size={15} />, agir: () => dialogue('Note sur l’incident', (fermer) => <NoteIncident i={i} fermer={fermer} />) })
      groupes.push({ actions: gestes })
      if (!clos && i.sourdine !== undefined) {
        const sourdine = async (jusqua: Sourdine | null) => {
          if (jusqua === 'night' && !(await confirm({ ...CONFIRMER_NUIT, details: [{ label: 'Incident', value: i.titre }] }))) return
          const err = await sourdineIncident(i.id, jusqua)
          if (err) { toast('error', err); return }
          toast('ok', jusqua === null ? 'Sourdine levée : les notifications reprennent.'
            : jusqua === '30m' ? 'En sourdine pour 30 min : Sonde vous le rappellera s’il est toujours ouvert.'
            : 'Coupé pour la nuit : Sonde vous le rappellera à 8 h s’il est toujours ouvert.')
          signalerEcriture()
        }
        groupes.push({ titre: i.sourdine ? `En sourdine jusqu’à ${fmtHeure(i.sourdine)}` : 'Sourdine', actions: [
          { id: 'rappel', libelle: 'Me rappeler dans 30 min', icone: <AlarmClock size={15} />, agir: () => sourdine('30m') },
          { id: 'nuit', libelle: 'Couper pour la nuit', icone: <BellOff size={15} />, agir: () => sourdine('night') },
          ...(i.sourdine ? [{ id: 'lever', libelle: 'Lever la sourdine', icone: <Bell size={15} />, agir: () => sourdine(null) }] : []),
        ] })
      }
    }
    groupes.push({ titre: 'Copier', actions: [
      { id: 'c-titre', libelle: 'Copier le titre', icone: <ClipboardCopy size={15} />, agir: () => copierTexte(i.titre, 'titre', toast) },
      { id: 'c-lien', libelle: 'Copier le lien', icone: <ClipboardCopy size={15} />, agir: () => copierTexte(absolu(fiche), 'lien', toast) },
    ] })
    return groupes
  }, [peutAcquitter, dialogue, toast, confirm])

  return {
    appareil: (a: AppareilMenu) => menu(() => actionsAppareil(a), a.nom),
    incident: (i: IncidentMenu) => menu(() => actionsIncident(i), i.titre),
  }
}
